import { useMemo, useState } from 'react';
import type { Book, ShelfStatus } from '@booknest/shared';

import { genreMeta } from '@/features/discover/genre-meta.js';
import { CollectionsSection } from '@/features/collections/components/CollectionsSection.js';
import { CollectionFormModal } from '@/features/collections/components/CollectionFormModal.js';

import { BookModal } from '../components/BookModal.js';
import {
  Bookcase,
  type BookcaseUnitData,
} from '../components/bookcase/Bookcase.js';
import { useMyShelf } from '../hooks/useMyShelf.js';

type ViewMode = 'status' | 'genre' | 'year';
type Density = 'comfy' | 'compact';

const STATUS_ORDER: ReadonlyArray<ShelfStatus> = [
  'reading',
  'wishlist',
  'done',
  'abandoned',
];
const STATUS_META: Record<ShelfStatus, { label: string; emoji: string; empty: string }> = {
  reading: {
    label: 'En cours',
    emoji: '🔖',
    empty: 'Rien en cours… pioche dans la wishlist',
  },
  wishlist: {
    label: 'Wishlist',
    emoji: '💌',
    empty: 'Aucun livre en attente',
  },
  done: {
    label: 'Terminés',
    emoji: '✨',
    empty: 'Aucun livre terminé pour le moment',
  },
  abandoned: {
    label: 'Abandonnés',
    emoji: '🍂',
    empty: 'Pas de livre abandonné',
  },
};

const DENSITY_STORAGE_KEY = 'booknest:bookcase-density';
const VIEW_MODE_STORAGE_KEY = 'booknest:bookcase-view-mode';

export function LibraryPage(): JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'status';
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'genre' || stored === 'year' ? stored : 'status';
  });
  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'comfy';
    return window.localStorage.getItem(DENSITY_STORAGE_KEY) === 'compact'
      ? 'compact'
      : 'comfy';
  });
  const [search, setSearch] = useState('');
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [openingBookId, setOpeningBookId] = useState<string | null>(null);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const { data, isLoading } = useMyShelf();
  const entries = data?.items ?? [];

  const selectedEntry =
    entries.find((e) => e.book.id === selectedBookId) ?? null;
  const selectedBook = selectedEntry?.book ?? null;

  const handleBookClick = (id: string): void => {
    if (openingBookId !== null) return;
    setOpeningBookId(id);
    window.setTimeout(() => setSelectedBookId(id), 380);
  };

  const handleClose = (): void => {
    setSelectedBookId(null);
    setOpeningBookId(null);
  };

  const updateViewMode = (next: ViewMode): void => {
    setViewMode(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    }
  };

  const updateDensity = (next: Density): void => {
    setDensity(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    }
  };

  const filteredBooks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) return entries;
    return entries.filter(
      (e) =>
        e.book.title.toLowerCase().includes(q) ||
        e.book.author.toLowerCase().includes(q) ||
        e.book.genre.toLowerCase().includes(q),
    );
  }, [entries, search]);

  const units: ReadonlyArray<BookcaseUnitData> = useMemo(() => {
    if (viewMode === 'status') {
      return STATUS_ORDER.map((status) => {
        const meta = STATUS_META[status];
        const books: Book[] = [];
        for (const e of filteredBooks) {
          if (e.status === status) books.push(e.book);
        }
        return {
          key: status,
          label: meta.label,
          emoji: meta.emoji,
          emptyMessage: meta.empty,
          books,
        };
      });
    }
    if (viewMode === 'genre') {
      const byGenre = new Map<string, Book[]>();
      for (const e of filteredBooks) {
        const list = byGenre.get(e.book.genre) ?? [];
        list.push(e.book);
        byGenre.set(e.book.genre, list);
      }
      return Array.from(byGenre.entries())
        .sort((a, b) => b[1].length - a[1].length)
        .map(([g, books]) => {
          const meta = genreMeta(g as Parameters<typeof genreMeta>[0]);
          return {
            key: g,
            label: meta.label,
            emoji: meta.emoji,
            emptyMessage: '',
            books,
          };
        });
    }
    // year
    const byYear = new Map<number, Book[]>();
    const undated: Book[] = [];
    for (const e of filteredBooks) {
      const y = e.book.year;
      if (y === null) {
        undated.push(e.book);
        continue;
      }
      const list = byYear.get(y) ?? [];
      list.push(e.book);
      byYear.set(y, list);
    }
    const yearUnits: BookcaseUnitData[] = Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, books]) => ({
        key: `year-${year}`,
        label: String(year),
        emoji: '📅',
        emptyMessage: '',
        books,
      }));
    if (undated.length > 0) {
      yearUnits.push({
        key: 'year-undated',
        label: 'Sans date',
        emoji: '📅',
        emptyMessage: '',
        books: undated,
      });
    }
    return yearUnits;
  }, [viewMode, filteredBooks]);

  const visibleUnits = units.filter((u) => u.books.length > 0);

  return (
    <div className={`flex flex-col gap-6 ${density === 'compact' ? 'density-compact' : ''}`}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-brown md:text-4xl xl:text-5xl">
            Mes étagères
          </h1>
          <p className="mt-1 font-script text-lg text-brown-soft xl:text-xl">
            ton meuble de lecture 📚
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreatingCollection(true)}
          className="flex items-center gap-1.5 rounded-full bg-paper px-4 py-2.5 text-sm font-semibold text-brown shadow-sm ring-1 ring-cream-dark hover:bg-cream-dark"
        >
          <span aria-hidden>＋</span>
          Nouvelle collection
        </button>
      </header>

      <CollectionsSection />

      <Toolbar
        viewMode={viewMode}
        onViewMode={updateViewMode}
        density={density}
        onDensity={updateDensity}
        search={search}
        onSearch={setSearch}
      />

      {isLoading ? (
        <p className="text-sm text-brown-soft">On déplie tes étagères…</p>
      ) : visibleUnits.length === 0 ? (
        <div className="rounded-3xl bg-paper p-8 text-center shadow-sm">
          <p className="text-3xl">🔍</p>
          <p className="mt-2 text-sm text-brown-soft">
            Aucun livre ne correspond
          </p>
        </div>
      ) : (
        <Bookcase
          units={visibleUnits}
          density={density}
          openingBookId={openingBookId}
          onBookClick={(b) => handleBookClick(b.id)}
        />
      )}

      <p className="text-center font-script text-base text-brown-soft">
        👆 touche une tranche pour ouvrir le livre
      </p>

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={handleClose}
        />
      )}

      {creatingCollection && (
        <CollectionFormModal
          existingId={null}
          onClose={() => setCreatingCollection(false)}
        />
      )}
    </div>
  );
}

interface ToolbarProps {
  viewMode: ViewMode;
  onViewMode: (m: ViewMode) => void;
  density: Density;
  onDensity: (d: Density) => void;
  search: string;
  onSearch: (s: string) => void;
}

function Toolbar({
  viewMode,
  onViewMode,
  density,
  onDensity,
  search,
  onSearch,
}: ToolbarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <div className="flex flex-wrap gap-1.5">
        <ToolbarButton
          active={viewMode === 'status'}
          onClick={() => onViewMode('status')}
          icon="📋"
          label="Par statut"
        />
        <ToolbarButton
          active={viewMode === 'genre'}
          onClick={() => onViewMode('genre')}
          icon="🎨"
          label="Par genre"
        />
        <ToolbarButton
          active={viewMode === 'year'}
          onClick={() => onViewMode('year')}
          icon="📅"
          label="Par année"
        />
      </div>

      <div className="inline-flex rounded-full bg-paper p-1 shadow-sm">
        <DensityButton
          active={density === 'comfy'}
          onClick={() => onDensity('comfy')}
          icon="⬜"
          label="Confortable"
        />
        <DensityButton
          active={density === 'compact'}
          onClick={() => onDensity('compact')}
          icon="▪️"
          label="Compact"
        />
      </div>

      <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-full bg-paper px-3.5 py-2 shadow-sm">
        <span aria-hidden className="text-brown-light">
          🔍
        </span>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Filtrer dans tes étagères…"
          className="flex-1 bg-transparent text-sm text-brown placeholder:text-brown-light focus:outline-none"
        />
      </div>
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
        active
          ? 'bg-brown text-cream-light ring-1 ring-brown'
          : 'bg-paper text-brown-soft hover:bg-cream-dark hover:text-brown'
      }`}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function DensityButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
        active
          ? 'bg-cream-dark text-brown'
          : 'text-brown-soft hover:bg-cream-dark hover:text-brown'
      }`}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
